import { useState, useRef, useEffect, useCallback } from 'react';
import { GraduationCap, Calendar, ExternalLink } from 'lucide-react';

interface Academician {
  id: number;
  name: string;
  academy: string;
  department: string;
  elected_year: number;
  title: string;
  summary?: string;
  school_name: string;
}

interface AcademicianDetailPanelProps {
  academicians: Academician[];
  loading: boolean;
}

export default function AcademicianDetailPanel({ academicians, loading }: AcademicianDetailPanelProps) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [overflowIds, setOverflowIds] = useState<Set<number>>(new Set());
  const summaryRefs = useRef<Map<number, HTMLParagraphElement>>(new Map());

  const measureOverflow = useCallback(() => {
    const next = new Set<number>();
    summaryRefs.current.forEach((el, id) => {
      if (!expandedIds.has(id) && el.scrollHeight > el.clientHeight + 1) {
        next.add(id);
      }
    });
    setOverflowIds(prev => {
      if (prev.size === next.size && [...prev].every(id => next.has(id))) return prev;
      return next;
    });
  }, [expandedIds]);

  useEffect(() => {
    measureOverflow();
  }, [academicians, measureOverflow]);

  const setSummaryRef = useCallback((id: number, el: HTMLParagraphElement | null) => {
    if (el) summaryRefs.current.set(id, el);
    else summaryRefs.current.delete(id);
  }, []);

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 按学院分组
  const grouped = academicians.reduce((acc, a) => {
    const key = a.academy || '其他';
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {} as Record<string, Academician[]>);

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse bg-gray-100 rounded-xl p-4">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (academicians.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <GraduationCap size={32} className="opacity-30 mx-auto mb-2" />
        <p>暂无院士数据</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([academy, members]) => (
        <div key={academy}>
          <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
            {academy} ({members.length}人)
          </h4>
          <div className="space-y-2">
            {members.map(a => {
              // 去掉简介开头的人名
              const summary = a.summary?.startsWith(a.name)
                ? a.summary.slice(a.name.length).replace(/^[，,：:\s]+/, '')
                : a.summary;
              return (
              <div key={a.id} className="bg-gray-50 rounded-xl p-3 hover:bg-amber-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">{a.name}</span>
                    <a
                      href={`https://baike.baidu.com/item/${encodeURIComponent(a.name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-amber-600 transition-colors"
                      title="百度百科"
                      onClick={e => e.stopPropagation()}
                    >
                      <ExternalLink size={12} />
                    </a>
                    <span className="text-sm text-gray-500">{a.department}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Calendar size={12} />
                    <span>{a.elected_year}年当选</span>
                  </div>
                </div>
                {summary && (
                  <div className="mt-1.5 relative">
                    {expandedIds.has(a.id) ? (
                      <p className="text-xs text-gray-400">
                        {summary}
                        <button
                          onClick={() => toggleExpand(a.id)}
                          className="inline text-xs text-amber-600 hover:text-amber-700 ml-1"
                        >收起</button>
                      </p>
                    ) : (
                      <div className="relative">
                        <p
                          ref={el => setSummaryRef(a.id, el)}
                          className="text-xs text-gray-400 line-clamp-3"
                        >
                          {summary}
                        </p>
                        {overflowIds.has(a.id) && (
                          <button
                            onClick={() => toggleExpand(a.id)}
                            className="absolute bottom-0 right-0 text-xs text-amber-600 hover:text-amber-700 bg-gradient-to-l from-gray-50 via-gray-50 to-transparent pl-6 pr-0.5"
                          >展开</button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
